import { apiUrl } from "../utils/api";

/**
 * Die eigenen Bank- und Gläubigerdaten des Maklerbüros.
 *
 * Ohne sie verweigert der SEPA-Export den Dienst — und zwar zu Recht: Im
 * Altsystem standen Kontoinhaber, IBAN und BIC als Literale im VBA-Code, und
 * als Gläubiger-ID der Platzhalter `DE00ZZZ00099999999`. Was hier eingetragen
 * wird, steht später in jeder Einreichungsdatei bei der Bank.
 */
export interface SepaCreditorConfig {
  id?: number;
  /** Kontoinhaber, wie er in der Datei erscheint (`Dbtr/Nm` bzw. `Cdtr/Nm`). */
  accountHolderName: string;
  iban: string;
  bic?: string | null;
  /** `InitgPty/Nm` — meist identisch mit dem Kontoinhaber. */
  initiatingPartyName?: string | null;
  /** Gläubiger-Identifikationsnummer. Nur für Lastschriften nötig. */
  creditorId?: string | null;
  creditTransferVersion?: string;
  directDebitVersion?: string;
  /** Vorlauf in Kalendertagen bis zur Ausführung. */
  creditTransferLeadDays?: number;
  /** Erst- und Einmallastschrift (FRST/OOFF). */
  directDebitLeadDaysFirst?: number;
  /** Folge- und letzte Lastschrift (RCUR/FNAL). */
  directDebitLeadDaysRecurrent?: number;
  /** Sammelbuchung auf dem eigenen Kontoauszug. */
  batchBooking?: boolean;
}

const fehlertext = async (res: Response): Promise<string> => {
  let msg = `Fehlgeschlagen: HTTP ${res.status}`;
  try {
    const d = await res.json();
    if (d?.message) msg = Array.isArray(d.message) ? d.message.join(", ") : String(d.message);
  } catch {
    /* Antwort ohne JSON — die Statuszeile bleibt die Meldung. */
  }
  return msg;
};

/** Liefert `null`, solange nichts hinterlegt ist. */
export const sepaCreditorConfigLaden = async (): Promise<SepaCreditorConfig | null> => {
  const res = await fetch(apiUrl("sepa/creditor-config"));
  if (!res.ok) throw new Error(await fehlertext(res));
  const text = await res.text();
  return text ? (JSON.parse(text) as SepaCreditorConfig) : null;
};

export const sepaCreditorConfigSpeichern = async (
  daten: SepaCreditorConfig,
): Promise<SepaCreditorConfig> => {
  const res = await fetch(apiUrl("sepa/creditor-config"), {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(daten),
  });
  if (!res.ok) throw new Error(await fehlertext(res));
  return (await res.json()) as SepaCreditorConfig;
};
